import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AI_ENGINE_CONFIG_FREQUENCIES } from "@/api/ai-engine-config/ai-engine-config.types";
import { Label } from "../../forms/Label/Label";
import { Select, type TSelectOption } from "@/components/forms";

type TFrequencyFilterSelectVariant = "filter" | "form";

type TFrequencyFilterSelectProps = {
  value?: string;
  onChange: (value: string) => void;
  variant?: TFrequencyFilterSelectVariant;
  isLabelShown?: boolean;
  customLabel?: string;
  customPlaceholder?: string;
  disabled?: boolean;
  isClearable?: boolean;
  name?: string;
  id?: string;
};

const FrequencyFilterSelect = ({
  value = "",
  onChange,
  variant = "filter",
  isLabelShown = true,
  customLabel,
  customPlaceholder,
  disabled = false,
  isClearable = true,
  name,
  id,
}: TFrequencyFilterSelectProps) => {
  const { t } = useTranslation("common");
  const frequencyOptions = useMemo<TSelectOption[]>(() => {
    if (variant === "form") {
      return AI_ENGINE_CONFIG_FREQUENCIES.map((item) => ({
        value: item,
        label: item,
      }));
    }

    return [
      { value: "weekly", label: t("FILTER.FREQUENCY_WEEKLY") },
      { value: "daily", label: t("FILTER.FREQUENCY_DAILY") },
      { value: "monthly", label: t("FILTER.FREQUENCY_MONTHLY") },
    ];
  }, [t, variant]);

  const fieldName =
    name ?? (variant === "form" ? "frequency" : "frequency-filter");
  const fieldId = id ?? fieldName;

  return (
    <div className="w-full">
      {isLabelShown && (
        <Label weight="medium" htmlFor={fieldId}>
          {customLabel ?? t("FILTER.FREQUENCY")}
        </Label>
      )}
      <Select
        name={fieldName}
        options={frequencyOptions}
        value={frequencyOptions.find((item) => item.value === value) ?? null}
        onChange={(option: TSelectOption | null) => {
          onChange(option?.value ?? "");
        }}
        getOptionLabel={(option) => option.label}
        getOptionValue={(option) => option.value}
        placeholder={
          customPlaceholder ??
          (variant === "form" ? "" : t("FILTER.SELECT_FREQUENCY"))
        }
        isClearable={isClearable}
        isDisabled={disabled}
      />
    </div>
  );
};

export default FrequencyFilterSelect;
